/**
 * Build Google / Outlook / Yahoo / ICS calendar links for an attendance event.
 * Similar to LinkedIn “Add to calendar” after RSVP.
 */

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Local wall time → YYYYMMDDTHHMMSS (no Z; calendar apps treat as local / floating). */
function toCalStamp(dateStr, timeStr, fallbackHour = 9, fallbackMin = 0) {
  const d = String(dateStr || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  let h = fallbackHour;
  let m = fallbackMin;
  const t = String(timeStr || '').trim();
  const tm = t.match(/^(\d{1,2}):(\d{2})/);
  if (tm) {
    h = Number(tm[1]);
    m = Number(tm[2]);
  }
  const [Y, Mo, D] = d.split('-').map(Number);
  return `${Y}${pad(Mo)}${pad(D)}T${pad(h)}${pad(m)}00`;
}

function addOneHour(stamp) {
  if (!stamp || stamp.length < 15) return stamp;
  const Y = Number(stamp.slice(0, 4));
  const Mo = Number(stamp.slice(4, 6)) - 1;
  const D = Number(stamp.slice(6, 8));
  const h = Number(stamp.slice(9, 11));
  const m = Number(stamp.slice(11, 13));
  const dt = new Date(Y, Mo, D, h, m, 0);
  dt.setHours(dt.getHours() + 1);
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
}

function encode(v) {
  return encodeURIComponent(String(v || '').trim());
}

export function formatEventWhen(event) {
  if (!event?.event_date) return 'Date TBA';
  const date = new Date(`${event.event_date}T00:00:00`);
  const dateLabel = Number.isNaN(date.getTime())
    ? event.event_date
    : date.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
  const start = String(event.start_time || '').slice(0, 5);
  const end = String(event.end_time || '').slice(0, 5);
  if (start && end) return `${dateLabel} · ${start} – ${end}`;
  if (start) return `${dateLabel} · ${start}`;
  return dateLabel;
}

export function formatEventPlace(event) {
  const location = (event?.location || '').trim();
  const country = (event?.country || '').trim();
  if (location && country && location.toLowerCase() !== country.toLowerCase()) {
    return `${location}, ${country}`;
  }
  return location || country || '';
}

/** An event closes after its end time, start time, or the end of its event date. */
export function isEventClosed(event, now = Date.now()) {
  if (event?.is_closed) return true;

  const date = String(event?.event_date || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;

  const rawTime = String(event?.end_time || event?.start_time || '23:59:59').trim();
  const time = /^\d{1,2}:\d{2}$/.test(rawTime) ? `${rawTime}:00` : rawTime;
  const dueAt = new Date(`${date}T${time}`).getTime();
  return Number.isFinite(dueAt) && dueAt < now;
}

export function buildCalendarLinks(event) {
  const title = event?.title || 'COOL Event';
  const details = event?.description || '';
  const location = formatEventPlace(event);
  const start = toCalStamp(event?.event_date, event?.start_time, 9, 0);
  let end = toCalStamp(event?.event_date, event?.end_time, 10, 0);
  if (!start) {
    return { google: '', outlook: '', yahoo: '', ics: '' };
  }
  if (!event?.end_time) {
    end = addOneHour(start);
  }

  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encode(title)}&dates=${start}/${end}&details=${encode(details)}&location=${encode(location)}`;

  const outlookEndDate = event.event_date;
  const outlookEndTime = event.end_time
    ? String(event.end_time).slice(0, 8)
    : (() => {
        const s = String(event.start_time || '09:00:00').slice(0, 5).split(':');
        const h = (Number(s[0]) + 1) % 24;
        return `${pad(h)}:${s[1] || '00'}:00`;
      })();
  const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encode(title)}&body=${encode(details)}&location=${encode(location)}&startdt=${encode(`${event.event_date}T${String(event.start_time || '09:00:00').slice(0, 8)}`)}&enddt=${encode(`${outlookEndDate}T${outlookEndTime}`)}`;

  const yahoo = `https://calendar.yahoo.com/?v=60&title=${encode(title)}&st=${start}&et=${end}&desc=${encode(details)}&in_loc=${encode(location)}`;

  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//COOL//Event Attendance//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${String(title).replace(/\n/g, ' ')}`,
    `DESCRIPTION:${String(details).replace(/\n/g, '\\n')}`,
    `LOCATION:${String(location).replace(/\n/g, ' ')}`,
    `UID:cool-attendance-${event.id || 'x'}@reallylesson.com`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  const icsBlob = new Blob([icsLines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const ics = URL.createObjectURL(icsBlob);

  return { google, outlook, yahoo, ics };
}

export function downloadIcs(event) {
  const links = buildCalendarLinks(event);
  if (!links.ics) return;
  const a = document.createElement('a');
  a.href = links.ics;
  a.download = `${(event.title || 'event').replace(/[^\w\-]+/g, '_')}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
